import React, { useState, useEffect } from "react";
import axios from "axios";
import { EmailGuard } from "../App";
import { AuthGuard } from "../App";
import { toast } from "react-toastify"; // Ensure ToastContainer is imported
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaTrashAlt, FaBell, FaSpinner } from 'react-icons/fa'; // Import icons
import { StandardURL } from "../App";

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    notification: "",
  });
  const [isLoading, setIsLoading] = useState(true); // State for initial data fetch
  const [isDeleting, setIsDeleting] = useState(null); // State for tracking which notification is deleting
  const [isSending, setIsSending] = useState(false); // State for sending new notification

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");
    axios
      .get(
        `${StandardURL}/iNyumba/inappnotifications/viewallnotifications`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        // Ensure the response is an array before setting the state
        setNotifications(Array.isArray(response.data.response) ? response.data.response : []); 
      })
      .catch((error) => {
        console.error("Error fetching notifications:", error);
        toast.error(error.response?.data?.response || "Failed to fetch notifications.");
        setNotifications([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteNotifications = (notificationId) => {
    if (!window.confirm("Are you sure you want to delete this notification?")) {
        return;
    }

    setIsDeleting(notificationId);
    const token = localStorage.getItem("token");
    axios
      .delete(
        `${StandardURL}/iNyumba/inappnotifications/erasenotification/${notificationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        fetchNotifications();
        toast.success(response.data.response);
      })
      .catch((error) => {
        console.error("Error deleting notification:", error);
        toast.error(error.response?.data?.response || "Failed to delete notification.");
      })
      .finally(() => {
        setIsDeleting(null);
      });
  };

  const handleAddNotifications = () => {
    if (!modalData.notification.trim()) {
        toast.error("Notification message cannot be empty.");
        return;
    }
    
    setIsSending(true);
    const token = localStorage.getItem("token");
    const { notification } = modalData;
    axios
      .post(
        `${StandardURL}/iNyumba/inappnotifications/sendtoall`,
        {
          notification,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        setIsModalOpen(false);
        fetchNotifications();
        setModalData({ notification: "" });
        toast.success(response.data.response);
      })
      .catch((error) => {
        console.error("Error adding notification:", error);
        toast.error(error.response?.data?.response || "Failed to send notification.");
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const handleModalInputChange = (e) => {
    const { name, value } = e.target;
    setModalData({ ...modalData, [name]: value });
  };

  // ===================================
  // Component JSX
  // ===================================
  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">

          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center mb-6">
                <FaBell className="mr-3 text-indigo-600" /> System Notifications
            </h1>
            
            <div className="flex justify-end mb-6">
                <button
                    onClick={() => {
                        setModalData({ notification: "" }); // Reset form when opening
                        setIsModalOpen(true);
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out flex items-center"
                >
                    <FaPlus className="mr-2" />
                    Send New Notification
                </button>
            </div>

            {/* Notifications Table/List */}
            <div className="bg-white shadow-xl rounded-xl overflow-hidden">
                {isLoading ? (
                    <div className="flex justify-center items-center p-12">
                         <FaSpinner className="animate-spin text-indigo-600 w-6 h-6 mr-3" />
                         <p className="text-gray-600">Loading notifications...</p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        <FaBell className="w-10 h-10 mx-auto mb-3" />
                        <p>No notifications found.</p>
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-3/4">
                                    Notification Message
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {notifications.map((notification) => (
                                <tr key={notification.notificationid} className="hover:bg-gray-50 transition duration-150">
                                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">
                                        {notification.notification}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                        <button
                                            onClick={() =>
                                                handleDeleteNotifications(notification.notificationid)
                                            }
                                            disabled={isDeleting === notification.notificationid}
                                            className="inline-flex items-center justify-center bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg shadow-sm transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isDeleting === notification.notificationid ? (
                                                <FaSpinner className="animate-spin w-4 h-4" />
                                            ) : (
                                                <FaTrashAlt className="w-4 h-4" />
                                            )}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
          </div>
        </div>

        {/* Modal Component */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Overlay */}
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => !isSending && setIsModalOpen(false)}></div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              {/* Modal Panel */}
              <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                      <FaBell className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Send New In-App Notification
                      </h3>
                      <div className="mt-4">
                        <textarea
                          rows="4"
                          name="notification"
                          value={modalData.notification}
                          onChange={handleModalInputChange}
                          placeholder="Type your notification message here (e.g., 'Rent is due on the 1st')."
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 border"
                          disabled={isSending}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Modal Actions */}
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    onClick={handleAddNotifications}
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
                    disabled={isSending || !modalData.notification.trim()}
                  >
                    {isSending ? (
                        <FaSpinner className="animate-spin mr-2" />
                    ) : (
                        <FaPlus className="mr-2" />
                    )}
                    {isSending ? "Sending..." : "Send to All"}
                  </button>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm transition duration-150"
                    disabled={isSending}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </AuthGuard>
    </EmailGuard>
  );
};

export default NotificationsPage;